import { useEffect, useMemo, useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import { DateRange } from 'react-day-picker';

import { MatterType, UsageMetricsWithTokens } from '../../../../../backend/src/types/types';
import { formatTableData } from '../../../../../backend/src/utils/usageDataFormatting';
import { Icon } from '../../components/icon';
import useGetUsageDataByMatter from '@/api/queries/analyticsQueries/useGetUsageDataByMatter';
import useGetUsageDataByUser from '@/api/queries/analyticsQueries/useGetUsageDataByUser';
import { useLazyUserMetadata } from '@/api/queries/useGetUserMetadata';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { TablePagination } from '@/new/components/table/table-pagination';

interface Row extends UsageMetricsWithTokens {
  id?: string;
  matterName?: string;
  matterCode?: string;
  userEmail?: string;
  userDisabled?: boolean;
  matterType?: MatterType;
  matterCreatedBy?: string;
}

const columnHelper = createColumnHelper<Row>();

const commonColumns = [
  columnHelper.accessor('pages', {
    header: () => <span>No. of Pages</span>,
  }),
  columnHelper.accessor('runs', {
    header: () => <span>No. of Runs</span>,
  }),
  columnHelper.accessor('facts', {
    header: () => <span>No. of Facts</span>,
  }),
  columnHelper.accessor('chronologies', {
    header: () => <span>No. of Chronologies</span>,
  }),
  columnHelper.accessor('threads', {
    header: () => <span>No. of Threads</span>,
  }),
  columnHelper.accessor('tokens', {
    header: () => <span>No. of Credits</span>,
  }),
];

function GroupSelector({ setTableGrouping }: { setTableGrouping: (g: 'user' | 'matter') => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center pl-1">
        <span className="iconify lucide--ellipsis-vertical"></span>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right">
        <DropdownMenuItem onClick={() => setTableGrouping('user')}>Group by users</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTableGrouping('matter')}>Group by matters</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Helper component to render last active date with lazy loading
function LastActiveCell({ userId, metadata }: { userId: string; metadata: ReturnType<typeof useLazyUserMetadata> }) {
  const { getMetadata, hasMetadata, isPending } = metadata;
  const userMetadata = getMetadata(userId);

  if (isPending(userId) || !hasMetadata(userId)) {
    return <div className="h-4 w-20 animate-pulse rounded-md bg-gray-200/80" />;
  }

  if (!userMetadata) {
    return <span className="text-gray-500">Never</span>;
  }

  const date = new Date(userMetadata.lastActiveAt * 1000);
  return <span>{moment(date).fromNow()}</span>;
}

export function UsageTable({
  filterDateRange,
  selectedMatterId,
  selectedUserId,
  organisationId,
  isViewingOtherOrg,
}: {
  filterDateRange: DateRange;
  selectedMatterId?: string;
  selectedUserId?: string;
  organisationId: string;
  isViewingOtherOrg?: boolean;
}) {
  const [tableGrouping, setTableGrouping] = useState<'matter' | 'user'>('matter');

  // Use lazy user metadata hook for last active dates
  const userMetadata = useLazyUserMetadata();
  const [, setMetadataRefresh] = useState(0);

  const userColumns = useMemo(
    () => [
      columnHelper.accessor('userEmail', {
        header: () => (
          <div className="flex items-center gap-1">
            Email Address
            <GroupSelector setTableGrouping={setTableGrouping} />
          </div>
        ),
        cell: (info) => info.getValue(),
      }),
      ...commonColumns,
      columnHelper.display({
        id: 'lastActiveAt',
        header: () => <span>Last Active</span>,
        cell: (info) => (
          <div className="w-28">
            <LastActiveCell userId={info.row.original.id ?? ''} metadata={userMetadata} />
          </div>
        ),
      }),
    ],
    [userMetadata],
  );

  const { data: usageDataByMatter, isLoading: isLoadingUsageDataByMatter } = useGetUsageDataByMatter(
    true,
    filterDateRange.from ? filterDateRange.from.toISOString() : '',
    filterDateRange.to ? filterDateRange.to.toISOString() : '',
    selectedMatterId,
    selectedUserId,
    organisationId,
  );

  const { data: usageDataByUser, isLoading: isLoadingUsageDataByUser } = useGetUsageDataByUser(
    true,
    filterDateRange.from ? filterDateRange.from.toISOString() : '',
    filterDateRange.to ? filterDateRange.to.toISOString() : '',
    selectedMatterId,
    selectedUserId,
    organisationId,
  );

  const tableData = useMemo(
    () => (tableGrouping === 'matter' ? formatTableData(usageDataByMatter) : formatTableData(usageDataByUser)),
    [tableGrouping, usageDataByMatter, usageDataByUser],
  );

  const matterColumns = useMemo(
    () => [
      columnHelper.accessor('matterName', {
        header: () => (
          <div className="flex items-center gap-1">
            Matter Name
            <GroupSelector setTableGrouping={setTableGrouping} />
          </div>
        ),
        cell: (info) => (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="line-clamp-1 max-w-54 overflow-ellipsis">
              {isViewingOtherOrg ? (info.row.original.id ?? '') : info.row.original.matterName}
            </span>

            {!isViewingOtherOrg && info.row.original.matterType === MatterType.Personal && (
              <span className="text-xs text-gray-500">{info.row.original.matterCreatedBy}</span>
            )}

            {!isViewingOtherOrg && info.row.original.matterCode && (
              <div className="rounded-md border bg-gray-100 px-1 py-0.25 text-xs whitespace-nowrap text-gray-600">
                {info.row.original.matterCode}
              </div>
            )}
          </div>
        ),
      }),
      ...commonColumns,
    ],
    [isViewingOtherOrg],
  );

  const tableColumns = useMemo(
    () => (tableGrouping === 'matter' ? matterColumns : userColumns),
    [tableGrouping, matterColumns, userColumns],
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'tokens',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
  });

  // Fetch metadata for currently visible users when grouped by user
  useEffect(() => {
    if (tableGrouping !== 'user') return;

    const visibleRows = table.getRowModel().rows;
    const visibleUserIds = visibleRows.map((row) => row.original.id).filter((id): id is string => !!id);

    if (visibleUserIds.length > 0) {
      userMetadata.fetchMetadata(visibleUserIds).then(() => {
        // Force a re-render to show the fetched metadata
        setMetadataRefresh((prev) => prev + 1);
      });
    }
  }, [table.getState().pagination.pageIndex, tableGrouping, tableData, userMetadata.cacheVersion]);

  return (
    <div>
      {isLoadingUsageDataByMatter || isLoadingUsageDataByUser ? (
        <div className="flex h-60 items-center justify-center p-8">
          <Icon className="lucide--loader-circle animate-spin" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination table={table} plural={tableGrouping === 'matter' ? 'matters' : 'users'} />
        </>
      )}
    </div>
  );
}
